import { useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { useDemoState } from "@/data/demoState";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  AlertTriangle, Clock, CheckCircle2, ArrowRight, Send, Users,
  Truck, Lock, Eye, FileText, Sparkles, ChevronRight, Mail,
  MessageSquare, Calendar, RotateCcw, ExternalLink, Info
} from "lucide-react";

const viewTabs = [
  { id: 'project', label: 'By Project' },
  { id: 'stage', label: 'By Stage' },
  { id: 'owner', label: 'By Owner' },
  { id: 'urgency', label: 'By Urgency' },
];

const severityConfig = {
  critical: { bg: 'bg-red-50', border: 'border-l-red-500', badge: 'bg-red-100 text-red-700', dot: 'bg-red-500 animate-pulse' },
  warning: { bg: 'bg-amber-50', border: 'border-l-amber-500', badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-400' },
  info: { bg: 'bg-blue-50', border: 'border-l-blue-500', badge: 'bg-blue-100 text-blue-700', dot: 'bg-blue-400' },
};

const statusLabels = {
  overdue: 'Overdue', 'no-progress': 'No progress', 'under-review': 'Under review', 'in-progress': 'In progress',
  pending: 'Pending', 'action-needed': 'Action needed',
};

const typeLabels = {
  'consultant-overdue': 'Consultant overdue', 'external-approval': 'External approval', 'proof-review': 'Proof review',
  'unassigned': 'Unassigned', 'consultant-handover': 'Consultant handover', 'council-request': 'Council request',
  'missing-cert': 'Missing certificate', 'weak-proof': 'Weak proof',
};

function BlockerCard({ blocker, onClick }) {
  const sev = severityConfig[blocker.severity] || severityConfig.warning;
  return (
    <button onClick={() => onClick(blocker)} className={`w-full text-left rounded-xl border-l-4 ${sev.border} bg-white border border-slate-200 p-4 hover:shadow-md transition-all group`}
      data-testid={`blocker-card-${blocker.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${sev.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge className={`text-[10px] ${sev.badge}`}>{blocker.severity}</Badge>
            <Badge className="text-[10px] bg-slate-100 text-slate-600">{typeLabels[blocker.type] || blocker.type}</Badge>
            <Badge className="text-[10px] bg-slate-100 text-slate-500">{statusLabels[blocker.status] || blocker.status}</Badge>
          </div>
          <p className="text-sm font-semibold text-slate-900 mb-1">{blocker.title}</p>
          <p className="text-xs text-slate-500">{blocker.project} · {blocker.stage} {blocker.conditionRef && `· Condition ${blocker.conditionRef}`}</p>
          <div className="flex items-center gap-4 mt-2 text-[11px] text-slate-500">
            <span className="flex items-center gap-1"><Users className="w-3 h-3" />{blocker.owner}</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Due {blocker.dueDate}</span>
            {blocker.trade && <span className="flex items-center gap-1"><Truck className="w-3 h-3" />{blocker.trade}</span>}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors shrink-0 mt-1" />
      </div>
    </button>
  );
}

export default function JobBlocker() {
  const { blockers, resolveBlocker, logAction } = useDemoState();
  const [activeView, setActiveView] = useState('project');
  const [selected, setSelected] = useState(null);

  const critical = blockers.filter(b => b.severity === 'critical').length;
  const warning = blockers.filter(b => b.severity === 'warning').length;
  const overdue = blockers.filter(b => b.status === 'overdue').length;

  const grouped = (() => {
    if (activeView === 'project') {
      const g = {};
      blockers.forEach(b => { if (!g[b.project]) g[b.project] = []; g[b.project].push(b); });
      return Object.entries(g);
    }
    if (activeView === 'stage') {
      const g = {};
      blockers.forEach(b => { if (!g[b.stage]) g[b.stage] = []; g[b.stage].push(b); });
      return Object.entries(g);
    }
    if (activeView === 'owner') {
      const g = {};
      blockers.forEach(b => { if (!g[b.owner]) g[b.owner] = []; g[b.owner].push(b); });
      return Object.entries(g);
    }
    const order = ['critical', 'warning', 'info'];
    const g = {};
    order.forEach(s => { const items = blockers.filter(b => b.severity === s); if (items.length) g[s] = items; });
    return Object.entries(g);
  })();

  const handleResolve = (blk) => {
    resolveBlocker(blk.id);
    setSelected(null);
    toast.success(`Blocker resolved: ${blk.title}`);
  };

  const handleChase = (blk) => {
    logAction(`Chase sent: ${blk.title} to ${blk.owner}`, 'chase');
    toast.success(`Chase sent to ${blk.owner}`, { description: blk.title });
  };

  return (
    <AppLayout>
      <div className="space-y-5" data-testid="job-blocker-page">
        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: 'Total blockers', value: blockers.length, color: 'text-slate-900' },
            { label: 'Critical', value: critical, color: 'text-red-600' },
            { label: 'Warning', value: warning, color: 'text-amber-600' },
            { label: 'Overdue', value: overdue, color: 'text-red-600' },
          ].map(kpi => (
            <Card key={kpi.label} className="border-slate-200">
              <CardContent className="py-3 px-4">
                <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">{kpi.label}</p>
                <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Copilot entry */}
        <Link to="/builder/job-blocker-copilot" data-testid="copilot-entry-blocker"
          className="flex items-center gap-3 px-4 py-3 bg-white border border-slate-200 rounded-lg hover:border-slate-300 hover:shadow-sm transition-all group">
          <Sparkles className="w-4 h-4 text-slate-900 shrink-0" />
          <span className="text-xs font-medium text-slate-600 flex-1">Ask the copilot to explain a blocker, draft a chase, or find the fastest path to clear.</span>
          <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-600 transition-colors shrink-0" />
        </Link>

        {/* View tabs */}
        <Tabs value={activeView} onValueChange={setActiveView}>
          <TabsList className="bg-slate-100">
            {viewTabs.map(t => <TabsTrigger key={t.id} value={t.id} className="text-xs" data-testid={`blocker-view-${t.id}`}>{t.label}</TabsTrigger>)}
          </TabsList>

          {viewTabs.map(t => (
            <TabsContent key={t.id} value={t.id} className="mt-4 space-y-5">
              {grouped.map(([group, items]) => (
                <div key={group}>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-sm font-semibold text-slate-900 capitalize">{group}</h3>
                    <Badge className="text-[10px] bg-slate-100 text-slate-600">{items.length}</Badge>
                  </div>
                  <div className="space-y-2">
                    {items.map(b => <BlockerCard key={b.id} blocker={b} onClick={setSelected} />)}
                  </div>
                </div>
              ))}
              {grouped.length === 0 && (
                <div className="text-center py-12">
                  <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
                  <p className="text-sm font-medium text-slate-700">No active blockers</p>
                  <p className="text-xs text-slate-400 mt-1">All blockers have been resolved in this scenario.</p>
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Detail sheet */}
      <Sheet open={!!selected} onOpenChange={() => setSelected(null)}>
        <SheetContent side="right" className="w-[520px] sm:max-w-[520px] overflow-y-auto" data-testid="blocker-detail-sheet">
          {selected && (() => {
            const sev = severityConfig[selected.severity] || severityConfig.warning;
            return (
              <div className="space-y-5">
                <SheetHeader>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={`text-[10px] ${sev.badge}`}>{selected.severity}</Badge>
                    <Badge className="text-[10px] bg-slate-100 text-slate-600">{typeLabels[selected.type]}</Badge>
                  </div>
                  <SheetTitle className="text-lg">{selected.title}</SheetTitle>
                  <SheetDescription>{selected.project} · {selected.stage}</SheetDescription>
                </SheetHeader>

                <div className="space-y-4">
                  {/* Why it matters */}
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Why this matters</p>
                    <p className="text-xs text-slate-600 leading-relaxed">{selected.consequence}</p>
                  </div>

                  {/* Details grid */}
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'Owner', value: `${selected.owner} (${selected.ownerRole})` },
                      { label: 'Due date', value: selected.dueDate },
                      { label: 'Status', value: statusLabels[selected.status] || selected.status },
                      { label: 'Chase count', value: `${selected.chaseCount} sent` },
                      { label: 'Trade impact', value: selected.tradeImpact || 'None' },
                      { label: 'Readiness impact', value: selected.readinessImpact || 'None' },
                    ].map(d => (
                      <div key={d.label} className="bg-white border border-slate-100 rounded-lg p-2.5">
                        <p className="text-[10px] font-medium text-slate-400 uppercase">{d.label}</p>
                        <p className="text-xs font-medium text-slate-700 mt-0.5">{d.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Timeline */}
                  {selected.timeline?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Timeline</p>
                      <div className="space-y-2">
                        {selected.timeline.map((t, i) => (
                          <div key={i} className="flex items-start gap-3 pl-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-1.5 shrink-0" />
                            <div>
                              <p className="text-[10px] text-slate-400">{t.date}</p>
                              <p className="text-xs text-slate-600">{t.action}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Separator />

                  {/* Actions */}
                  <div className="space-y-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Quick actions</p>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" onClick={() => handleChase(selected)} className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="blocker-chase-btn">
                        <Send className="w-3.5 h-3.5" />Send chase
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleChase(selected)} className="gap-1.5">
                        <MessageSquare className="w-3.5 h-3.5" />WhatsApp reminder
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleChase(selected)} className="gap-1.5">
                        <Calendar className="w-3.5 h-3.5" />Add to calendar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleResolve(selected)} className="gap-1.5 text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid="blocker-resolve-btn">
                        <CheckCircle2 className="w-3.5 h-3.5" />Mark resolved
                      </Button>
                    </div>
                  </div>

                  {/* Deep links */}
                  <div className="flex flex-wrap gap-2">
                    <Link to="/builder/stage-readiness" className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:border-blue-300 hover:text-blue-600 transition-all">
                      <Lock className="w-3 h-3" />Stage Readiness
                    </Link>
                    <Link to="/builder/trade-release" className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:border-blue-300 hover:text-blue-600 transition-all">
                      <Truck className="w-3 h-3" />Trade Release
                    </Link>
                    <Link to="/builder/job-blocker-copilot" className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:border-blue-300 hover:text-blue-600 transition-all">
                      <Sparkles className="w-3 h-3" />Ask Copilot
                    </Link>
                  </div>
                </div>
              </div>
            );
          })()}
        </SheetContent>
      </Sheet>
    </AppLayout>
  );
}
